/* ==========================================================================
   <rt-scroller> — dot indicators for R&T swipe rows

   Wraps a `.rt-scroller` track. When the track actually overflows (i.e. on
   mobile in "swipe" mode) it builds one dot per card, highlights the card
   in view, and lets people tap a dot to jump to it. On desktop, or in
   "stacked" mode, nothing overflows so the dots stay hidden.

   Markup (output by rt-steps / rt-card-row):
     <rt-scroller>
       <div class="rt-scroller rt-scroller--mobile" data-rt-track> …cards… </div>
       <div class="rt-scroller-dots" data-rt-dots hidden></div>
     </rt-scroller>

   Safe to include more than once on a page: it only defines itself once.
   ========================================================================== */

(function () {
  if (customElements.get('rt-scroller')) return;

  class RtScroller extends HTMLElement {

    connectedCallback() {
      this.track = this.querySelector('[data-rt-track]');
      this.dotsWrap = this.querySelector('[data-rt-dots]');

      if (!this.track || !this.dotsWrap) return;

      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);
      this.ticking = false;

      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      // Re-check when the screen size changes (desktop grid <-> mobile swipe)
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);

      // Re-check when cards are added, removed or reordered in the theme editor
      this.mutationObserver = new MutationObserver(this.update);
      this.mutationObserver.observe(this.track, { childList: true });

      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
    }

    /* Each direct child of the track is one card */
    get slides() {
      return Array.from(this.track.children);
    }

    /* Rebuild dots if needed and show/hide them */
    update() {
      const slides = this.slides;
      const overflowing = this.track.scrollWidth > this.track.clientWidth + 1;

      this.dotsWrap.hidden = !overflowing || slides.length < 2;

      if (this.dotsWrap.hidden) {
        this.dotsWrap.replaceChildren();
        return;
      }

      if (this.dotsWrap.children.length !== slides.length) {
        const dots = slides.map((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.setAttribute('aria-label', `Show item ${index + 1} of ${slides.length}`);
          dot.addEventListener('click', () => this.goTo(index));
          return dot;
        });

        this.dotsWrap.replaceChildren(...dots);
      }

      this.setActive(this.currentIndex());
    }

    /* Throttle scroll updates to one per animation frame */
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;

      requestAnimationFrame(() => {
        this.setActive(this.currentIndex());
        this.ticking = false;
      });
    }

    /* Which card is closest to the left edge of the track? */
    currentIndex() {
      const slides = this.slides;
      const track = this.track;

      // Scrolled right to the end: the last card counts as active
      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) {
        return slides.length - 1;
      }

      const trackLeft = track.getBoundingClientRect().left
        + parseFloat(getComputedStyle(track).paddingLeft || 0);

      let closest = 0;
      let closestDistance = Infinity;

      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = index;
        }
      });

      return closest;
    }

    setActive(activeIndex) {
      Array.from(this.dotsWrap.children).forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;

      const trackLeft = this.track.getBoundingClientRect().left
        + parseFloat(getComputedStyle(this.track).paddingLeft || 0);
      const offset = slide.getBoundingClientRect().left - trackLeft;

      this.track.scrollBy({ left: offset, behavior: 'smooth' });
    }
  }

  customElements.define('rt-scroller', RtScroller);
})();
